"use client";

import Link from "next/link";
import Image from "next/image";
import CopyrightsInfo from "@/components/global/CopyrightsInfo";
import { useState } from "react";
import styles from "./css/Footer.module.css";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");

    // Regex to ensure email has a complete domain with a TLD
    const emailRegex = /^[^@]+@([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      setIsSubmitting(false);
      return;
    }

    try {
      const apiUrl = process.env.NEXT_PUBLIC_API_URL;
      if (!apiUrl) {
        setError("API URL is not configured");
        setIsSubmitting(false);
        return;
      }
      const response = await fetch(`${apiUrl}/newsletter/subscribe`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || "Subscription failed");
      }

      setIsSuccess(true);
      setTimeout(() => {
        setIsSuccess(false);
        setEmail("");
      }, 5000); // Reset after 5 seconds
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="theme-footer-seven mt-120 md-mt-100">
      <div className="lg-container">
        <div className="container">
          <div className="row">
            <div className="col-xl-3 col-lg-2 mb-40" data-aos="fade-up">
              <div className="logo">
                <Link href="/">
                  <Image src="/images/logo/av_logo_footer.svg" alt="Avelon Logo" width={180} height={100} />
                </Link>
              </div>
            </div>
            <div className="col-lg-2 col-md-6 mb-40" data-aos="fade-up" data-aos-delay="100">
              <h5 className="title">Links</h5>
              <ul className="footer-list">
                <li><Link href="/">Home</Link></li>
                <li><Link href="/marketplace">Marketplace</Link></li>
                <li><Link href="/company">About us</Link></li>
                <li><Link href="/support">Support</Link></li>
              </ul>
            </div>
            <div className="col-lg-3 col-md-6 mb-40" data-aos="fade-up" data-aos-delay="150">
              <h5 className="title">Legal</h5>
              <ul className="footer-list">
                <li><Link href="/terms">Terms of Service</Link></li>
                <li><Link href="/privacy">Privacy Policy</Link></li>
                <li><Link href="/refund">Refund Policy</Link></li>
              </ul>
            </div>
            <div className="col-xl-4 col-lg-5 mb-40" data-aos="fade-up" data-aos-delay="200">
              <div className="newsletter">
                <h5 className="title">Newsletter</h5>
                <p>Join over <span>26,000</span> people getting our emails</p>
                <form onSubmit={handleSubmit}>
                  <input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setError(""); // Clear error when user starts typing
                    }}
                    required
                    disabled={isSubmitting || isSuccess}
                  />
                  <button
                    type="submit"
                    className={`dark-btn ${styles["dark-btn"]} ${isSuccess ? styles["success-btn"] : ""}`}
                    disabled={isSubmitting || isSuccess}
                  >
                    {isSuccess ? (
                      <svg
                        className={styles["check-icon"]}
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M2 10L8 16L18 4"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    ) : (
                      "Sign Up"
                    )}
                  </button>
                  {error && <div className={styles["error-text"]}>{error}</div>}
                </form>
                <div className={`info ${styles.info} ${error ? styles["info-with-error"] : ""}`}>
                  We only send interesting and relevant emails.
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="container">
          <div className="bottom-footer">
            <div className="row">
              <div className="col-lg-4 order-lg-1 mb-20">
                <ul className="d-flex justify-content-center justify-content-lg-start footer-nav">
                  <li><Link href="/terms">Privacy & Terms</Link></li>
                  <li><Link href="/support">Contact Us</Link></li>
                </ul>
              </div>
              <div className="col-lg-4 order-lg-3 mb-20">
                <ul className="d-flex justify-content-center justify-content-lg-end social-icon">
                  <li><Link href="https://www.facebook.com/AvelonPR/" target="new"><i className="fa fa-facebook" aria-hidden="true"></i></Link></li>
                  <li><Link href="https://x.com/avelonpr" target="new"><i className="fa fa-twitter" aria-hidden="true"></i></Link></li>
                  <li><Link href="https://www.linkedin.com/company/avelon-pr/" target="new"><i className="fa fa-linkedin" aria-hidden="true"></i></Link></li>
                </ul>
              </div>
              <div className="col-lg-4 order-lg-2 mb-20">
                <CopyrightsInfo />
              </div>
            </div>
            <div className="row" data-nosnippet>
              <div className="col-md-12 disclaimer">
                AvelonPR OÜ utilizes logos, trademarks, and names of media outlets—such as online publications, print magazines, news organizations, and other editorial platforms (collectively, &apos;Media Entities&apos;)—solely for illustrative and informational purposes to indicate potential placement opportunities offered through our services. We expressly disclaim any ownership, endorsement, affiliation, partnership, or agency relationship with these Media Entities and do not represent or act on their behalf in any capacity. This use does not imply any authority to speak for, bind, or otherwise represent said Media Entities, nor does it guarantee publication therein. For full details, please refer to our <Link href="/terms" target="_self">Terms of Service</Link>.
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}